/**
 * SSML 辞書マスタの新規作成 / 編集モーダル (SBI-02)。
 *
 * 放送地点マスタの編集モーダル (`BroadcastPointForm`) と同じ骨格
 * (`.modal-backdrop` > `.modal` > `.modal-header` / `.modal-body` / `.modal-footer`) で表示する。
 *
 * - 新規 (`mode='create'`) は POST、編集 (`mode='edit'`) は PUT で保存する。
 *   編集中は `language_code` / `source_text` がキーなので変更できない
 *   (変えたいときは削除して登録し直す)。
 * - 入力の妥当性検証はサーバ側が正源。400 等のメッセージは日本語に変換して表示し、
 *   モーダルは開いたままにして入力内容を失わないようにする。
 *   - 原因の入力欄が特定できるもの: 放送地点マスタと同じく、その欄に
 *     ブラウザ標準の入力検証の吹き出し (`setCustomValidity` + `reportValidity`) +
 *     赤枠 (`.is-invalid`) + 欄の直下の文で表示し、その欄へフォーカスする。
 *     入力し直すと解除する。
 *   - 特定できないもの (認証切れ / サーバエラー / 通信断 など): ヘッダー直下のバナーに表示する。
 * - 必須項目 (変換元 / 置換後) は `required`。保存ボタンは常に押せて、空のまま押すと
 *   ブラウザ標準の「このフィールドに入力してください。」の吹き出しが出る (放送地点マスタと同じ)。
 * - 保存に成功したら `onSaved` を呼ぶ。閉じる / 一覧の再取得は親の責務。
 * - 新規登録で、同じ言語・同じ変換元が既に登録済みの場合 (種別が違っても同じキーで上書きされる)、
 *   保存の前に「上書きしますか？」の確認ダイアログを出す。判定は親が渡す一覧 (`existingEntries`)
 *   で行う (サーバ問い合わせは増やさない)。サーバは常に上書き保存するため、一覧が古くて
 *   確認が出なかった場合でもデータは壊れない。
 * - 背景クリック・ESC では閉じない (誤操作で入力を失わないため)。
 * - 試聴は既存の `POST /audio-preview` を流用する。保存前でも聴けるため、
 *   アクセントの効き具合を確認してから登録できる。保存はしない。
 */
import { Spinner } from './Spinner';
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

import { base64ToAudioUrl } from '../api/broadcastPoints';
import type { BroadcastPointsClient } from '../api/broadcastPoints';
import type { SsmlDictionaryClient } from '../api/ssmlDictionary';
import type {
  SsmlDictionaryEntry,
  SsmlDictionarySaveResult,
  SsmlDictionaryType,
} from '../api/types';
import { PHRASE_TEMPLATE_LANGUAGE_OPTIONS } from '../data/phraseTemplates';
import {
  ACCENT_CAUTION_MESSAGE,
  ACCENT_MARK_MISSING_CONFIRM_MESSAGE,
  SSML_DICTIONARY_TYPE_DEFINITIONS,
  SSML_DICTIONARY_TYPE_ORDER,
  buildDictionaryPreviewInput,
  dictionaryTypeLabel,
  needsAccentMarkConfirmation,
} from '../data/ssmlDictionary';

import { ConfirmDialog } from './ConfirmDialog';
import { CopyableToken } from './CopyableToken';
import {
  REQUIRED_FIELD_MESSAGES,
  describeApiError,
  type SsmlDictionaryErrorField,
} from './ssmlDictionaryMessages';
import { StatusBanner } from './StatusBanner';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { judgeRemoteChange } from '../utils/remoteChange';

/** 変換元の最大長 (バックエンド `MAX_SSML_DICTIONARY_SOURCE_LENGTH` と一致させる) */
const MAX_SOURCE_LENGTH = 100;
/** 置換後の最大長 (バックエンド `MAX_SSML_DICTIONARY_REPLACEMENT_LENGTH` と一致させる) */
const MAX_REPLACEMENT_LENGTH = 200;
/** 補足の最大長 (バックエンド `MAX_SSML_DICTIONARY_NOTE_LENGTH` と一致させる) */
const MAX_NOTE_LENGTH = 500;

/** 入力フォームの状態 */
interface FormState {
  languageCode: string;
  sourceText: string;
  dictionaryType: SsmlDictionaryType;
  replacement: string;
  note: string;
  enabled: boolean;
}

function createInitialForm(entry: SsmlDictionaryEntry | null): FormState {
  if (entry) {
    return {
      languageCode: entry.language_code,
      sourceText: entry.source_text,
      dictionaryType: entry.dictionary_type,
      replacement: entry.replacement,
      note: entry.note ?? '',
      enabled: entry.enabled,
    };
  }
  return {
    languageCode: PHRASE_TEMPLATE_LANGUAGE_OPTIONS[0]?.key ?? 'ja',
    sourceText: '',
    dictionaryType: 'accent',
    replacement: '',
    note: '',
    enabled: true,
  };
}

/** 入力内容が同じか (前後の空白は無視して比較する) */
function isSameForm(a: FormState, b: FormState): boolean {
  return (
    a.languageCode === b.languageCode &&
    a.sourceText.trim() === b.sourceText.trim() &&
    a.dictionaryType === b.dictionaryType &&
    a.replacement.trim() === b.replacement.trim() &&
    a.note.trim() === b.note.trim() &&
    a.enabled === b.enabled
  );
}

/** 欄ごとのエラー文 (キーが無い欄はエラーなし) */
type FieldErrors = Partial<Record<SsmlDictionaryErrorField, string>>;

/** 入力欄の直下に出す、原因欄のエラー文 (赤枠 `.is-invalid` と対)。 */
function FieldError(props: { id: string; message: string | undefined }): React.JSX.Element | null {
  if (!props.message) return null;
  return (
    <span id={props.id} className="field-error" data-testid={props.id}>
      {props.message}
    </span>
  );
}

/** エラー欄の走査順 (画面の上から下) */
const FIELD_ORDER: readonly SsmlDictionaryErrorField[] = [
  'language',
  'source',
  'replacement',
  'note',
];

/** 入力検証 API (`setCustomValidity` など) を持つ入力要素 */
type ValidatableElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * フォームの項目を変えたとき、エラーを解除すべき欄。
 *
 * 欄どうしに依存する規則があるため、編集した欄だけでなく関連する欄も解除する。
 * 解除しないと、`setCustomValidity` が残ったまま標準の入力検証が送信をブロックし続け、
 * 「置換後は変換元と異なる文字列に」のエラーを変換元の修正で直しても保存できなくなる。
 *   - 変換元: 「置換後は変換元と異なる」規則が置換後に影響する
 *   - 種別: 置換後の許可文字の規則が変わる
 */
function errorFieldsOfFormKey(key: keyof FormState): SsmlDictionaryErrorField[] {
  switch (key) {
    case 'languageCode':
      return ['language'];
    case 'sourceText':
      return ['source', 'replacement'];
    case 'replacement':
    case 'dictionaryType':
      return ['replacement'];
    case 'note':
      return ['note'];
    default:
      return [];
  }
}

export interface SsmlDictionaryFormModalProps {
  /** 新規 (create) / 編集 (edit) */
  mode: 'create' | 'edit';
  /** 編集対象 (mode='edit' のとき必須)。新規のときは null */
  entry: SsmlDictionaryEntry | null;
  /**
   * 現在の登録済み一覧。新規登録時の上書き確認の判定に使う
   * (省略時は確認しない)。
   */
  existingEntries?: readonly SsmlDictionaryEntry[];
  apiClient: SsmlDictionaryClient;
  /** 試聴用 (`POST /audio-preview`)。放送地点 API のクライアントを流用する */
  previewClient: BroadcastPointsClient;
  /** 保存成功時に呼ばれる (サーバの warnings を含む結果を渡す) */
  onSaved: (result: SsmlDictionarySaveResult) => void;
  /**
   * 編集中に他者が削除していたとき、「他者によって削除されています」のお知らせを閉じた後に呼ばれる
   * (画面側でモーダルを閉じて一覧を取り直す)。
   */
  onDeletedByOthers: () => void;
  /** 閉じる (キャンセル / ×) */
  onClose: () => void;
}

export function SsmlDictionaryFormModal(props: SsmlDictionaryFormModalProps): React.JSX.Element {
  const {
    mode,
    entry,
    existingEntries = [],
    apiClient,
    previewClient,
    onSaved,
    onDeletedByOthers,
    onClose,
  } = props;
  const isEditing = mode === 'edit';

  const [form, setForm] = useState<FormState>(() => createInitialForm(entry));
  /** 開いたときの入力内容 (編集時に「何か変更したか」を判定する基準 / Issue #765) */
  const [initialForm] = useState<FormState>(() => createInitialForm(entry));
  /** 開いたときの更新日時 (LastUpdate)。保存時に他者の更新 / 削除を判定する基準 (Issue #765) */
  const [baselineUpdatedAt] = useState<string | null>(() => entry?.updated_at ?? null);
  const [saving, setSaving] = useState(false);
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();
  /** 開いたときから入力内容を変更しているか */
  const dirty = !isSameForm(form, initialForm);
  /** 上書き確認ダイアログの対象 (null = 表示しない) */
  const [overwriteTarget, setOverwriteTarget] = useState<SsmlDictionaryEntry | null>(null);
  /** 「アクセント核が入っていません」の保存確認ダイアログを開いているか */
  const [accentMarkConfirmOpen, setAccentMarkConfirmOpen] = useState(false);
  /** 原因の欄が特定できないエラー (ヘッダー直下のバナーに出す) */
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  /** 原因の欄が特定できたエラー (該当欄に出す) */
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  /** 破棄すべき試聴 URL (object URL の解放用) */
  const previewUrlRef = useRef<string | null>(null);
  /** 各入力欄の DOM (ブラウザ標準の入力検証の吹き出し / フォーカス用) */
  const fieldRefs = useRef<Partial<Record<SsmlDictionaryErrorField, ValidatableElement | null>>>(
    {},
  );
  /** バナーの DOM (表示時にスクロールして見える位置に持ってくる) */
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const typeDefinition = SSML_DICTIONARY_TYPE_DEFINITIONS[form.dictionaryType];

  /** アンマウント時に試聴の object URL を解放する。 */
  useEffect(() => {
    return () => {
      const url = previewUrlRef.current;
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  /** 試聴音声を破棄する (フォーム内容が変わると古い音声は誤解のもとになる)。 */
  const clearPreview = useCallback((): void => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewError(null);
  }, []);

  const updateForm = useCallback(
    (patch: Partial<FormState>): void => {
      setForm((prev) => ({ ...prev, ...patch }));
      // 入力し直した欄のエラーは解除する (再保存で再検証される)。
      setFieldErrors((prev) => {
        if (Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        for (const key of Object.keys(patch) as (keyof FormState)[]) {
          for (const field of errorFieldsOfFormKey(key)) delete next[field];
        }
        return next;
      });
      // 入力を変えたら、以前の試聴音声は今の入力と食い違うので破棄する。
      clearPreview();
    },
    [clearPreview],
  );

  /**
   * 欄ごとのエラーを、ブラウザ標準の入力検証 (`setCustomValidity`) に反映する。
   * エラーがあれば先頭の欄にフォーカスし、標準の吹き出しを出す。
   * (放送地点マスタの `required` 欄と同じ見た目になる)
   */
  useEffect(() => {
    let first: ValidatableElement | null = null;
    for (const field of FIELD_ORDER) {
      const el = fieldRefs.current[field];
      if (!el) continue;
      const message = fieldErrors[field];
      el.setCustomValidity(message ?? '');
      if (message && first === null) first = el;
    }
    if (first) {
      first.focus();
      first.reportValidity();
    }
  }, [fieldErrors]);

  /** バナーが出たら、見える位置までスクロールする。 */
  useEffect(() => {
    const el = bannerRef.current;
    if (errorMessage && el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [errorMessage]);

  /** 実際に保存する (新規 = POST / 編集 = PUT)。入力チェックと上書き確認は通過済みの前提。 */
  const executeSave = useCallback(async (): Promise<void> => {
    setSaving(true);
    setErrorMessage(null);
    setFieldErrors({});
    try {
      const result = isEditing
        ? await apiClient.update(form.languageCode, form.sourceText, {
            dictionaryType: form.dictionaryType,
            replacement: form.replacement,
            // 空文字も送る (サーバ側で「補足のクリア」になる)
            note: form.note,
            enabled: form.enabled,
          })
        : await apiClient.create({
            languageCode: form.languageCode,
            sourceText: form.sourceText,
            dictionaryType: form.dictionaryType,
            replacement: form.replacement,
            note: form.note,
            enabled: form.enabled,
          });
      onSaved(result);
    } catch (err) {
      const info = describeApiError(err);
      if (info.field) {
        setFieldErrors({ [info.field]: info.message });
      } else {
        setErrorMessage(info.message);
      }
      setSaving(false);
    }
  }, [apiClient, form, isEditing, onSaved]);

  /**
   * 保存ボタン: 入力チェック → (新規で既存と重複なら) 上書き確認 → 保存。
   * サーバ側のキーは trim 後の変換元なので、重複判定も trim して比較する。
   *
   * 新規登録には「実行してよろしいですか」は出さない (Issue #765)。出るのは上書き確認 (同じキーが登録済み) だけ。
   * 編集 (更新) は、開いてから他者が更新 / 削除していないかを確認してから保存する。
   * 自分が何も変更していなければ他者の更新 / 削除の確認は不要 (`hasChanges: false`)。
   */
  const handleSave = useCallback((): void => {
    // ブラウザ標準の `required` は空白だけの入力を通すので、ここでも同じ文言で止める。
    const missing: FieldErrors = {};
    if (form.sourceText.trim() === '') missing.source = REQUIRED_FIELD_MESSAGES.source;
    if (form.replacement.trim() === '') missing.replacement = REQUIRED_FIELD_MESSAGES.replacement;
    if (Object.keys(missing).length > 0) {
      setErrorMessage(null);
      setFieldErrors(missing);
      return;
    }

    if (!isEditing) {
      const source = form.sourceText.trim();
      const existing = existingEntries.find(
        (e) => e.language_code === form.languageCode && e.source_text === source,
      );
      if (existing) {
        setOverwriteTarget(existing);
        return;
      }
    }
    if (!isEditing) {
      void executeSave();
      return;
    }
    void guardedUpdate({
      hasChanges: dirty,
      fetchRemoteState: async () => {
        const res = await apiClient.list();
        return judgeRemoteChange(
          baselineUpdatedAt,
          res.items.find(
            (e) => e.language_code === form.languageCode && e.source_text === form.sourceText,
          ),
        );
      },
      execute: () => void executeSave(),
      // 他者に削除されていたら保存できない。お知らせを閉じたら、画面側でモーダルを閉じて一覧を更新する。
      onDeleted: onDeletedByOthers,
    });
  }, [
    apiClient,
    baselineUpdatedAt,
    dirty,
    executeSave,
    existingEntries,
    form,
    guardedUpdate,
    isEditing,
    onDeletedByOthers,
  ]);

  /**
   * 保存ボタン / Enter 送信の入口。
   *
   * アクセント核は任意だが、入れ忘れを防ぐため、無いときは保存せずに確認ダイアログを出す
   * (「保存する」で `handleSave` = 入力チェック → 上書き確認 → 保存)。新規・編集のどちらでも同じ。
   * 置換後が空のときは別の入力不備なので確認せず、`handleSave` の必須チェックに任せる。
   */
  const requestSave = useCallback((): void => {
    if (needsAccentMarkConfirmation(form.dictionaryType, form.replacement)) {
      setAccentMarkConfirmOpen(true);
      return;
    }
    handleSave();
  }, [form.dictionaryType, form.replacement, handleSave]);

  /** アクセント核なしの保存確認で「保存する」が押されたとき */
  const handleConfirmSaveWithoutAccentMark = useCallback((): void => {
    setAccentMarkConfirmOpen(false);
    handleSave();
  }, [handleSave]);

  /**
   * 閉じる (キャンセル / ×): 登録 / 更新せずに閉じるので破棄の確認を挟む (Issue #765)。
   * 新規は常に確認する。編集は、開いたときから何か変更したときだけ確認する。
   */
  const handleClose = useCallback((): void => {
    if (!isEditing || dirty) {
      confirmDiscard(onClose);
      return;
    }
    onClose();
  }, [confirmDiscard, dirty, isEditing, onClose]);

  /** 入力中の内容を試聴する (保存はしない)。 */
  const handlePreview = useCallback(async (): Promise<void> => {
    const input = buildDictionaryPreviewInput(
      form.dictionaryType,
      form.sourceText,
      form.replacement,
    );
    if (input === null) {
      setPreviewError('試聴するには置換後を入力してください');
      return;
    }
    clearPreview();
    setPreviewing(true);
    try {
      // SBI-03: 入力中の読み・アクセントそのものを聴く画面なので、保存済みの辞書は適用しない
      // (適用すると、別の登録語が入力中の読みを書き換えて「入力した内容」と違う音になる)。
      const res = await previewClient.previewAudio({
        ...input,
        language: form.languageCode,
        applyDictionary: false,
      });
      const url = base64ToAudioUrl(res.content_base64, res.content_type);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (err) {
      setPreviewError(describeApiError(err).message);
    } finally {
      setPreviewing(false);
    }
  }, [clearPreview, form, previewClient]);

  const title = isEditing ? '辞書を編集' : '辞書を新規作成';

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="ssml-dictionary-modal"
      // 背景クリック / ESC では閉じない (onClick 未設定)
    >
      <form
        className="modal modal--single-column"
        style={{ maxWidth: 640 }}
        aria-label={isEditing ? '辞書の編集' : '辞書の登録'}
        data-testid="ssml-dictionary-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) requestSave();
        }}
      >
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }} data-testid="ssml-dictionary-form-title">
            {title}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            aria-label="閉じる"
            data-testid="ssml-dictionary-modal-close"
          >
            ×
          </button>
        </div>

        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            {errorMessage ? (
              <div ref={bannerRef}>
                <StatusBanner
                  variant="error"
                  onDismiss={() => setErrorMessage(null)}
                  data-testid="ssml-dictionary-form-error"
                >
                  {errorMessage}
                </StatusBanner>
              </div>
            ) : null}

            <p className="form-required-hint">
              <span className="required-mark">必須</span> がついた項目は必ず入力してください
            </p>

            <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
              <legend style={{ fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 4 }}>
                種別
              </legend>
              <div className="ssml-dictionary-type-group">
                {SSML_DICTIONARY_TYPE_ORDER.map((type) => (
                  <label key={type} className="sort-filter-radio ssml-dictionary-type-option">
                    <input
                      type="radio"
                      name="ssml-dictionary-type"
                      value={type}
                      checked={form.dictionaryType === type}
                      onChange={() => updateForm({ dictionaryType: type })}
                      data-testid={`ssml-dictionary-type-${type}`}
                    />
                    {SSML_DICTIONARY_TYPE_DEFINITIONS[type].label}
                  </label>
                ))}
              </div>
            </fieldset>

            <label>
              <span className="required">言語</span>
              <select
                data-testid="ssml-dictionary-language"
                value={form.languageCode}
                disabled={isEditing || saving}
                ref={(el) => {
                  fieldRefs.current.language = el;
                }}
                className={fieldErrors.language ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.language ? true : undefined}
                aria-describedby={
                  fieldErrors.language ? 'ssml-dictionary-language-error' : undefined
                }
                onChange={(e) => updateForm({ languageCode: e.target.value })}
              >
                {PHRASE_TEMPLATE_LANGUAGE_OPTIONS.map((language) => (
                  <option key={language.key} value={language.key}>
                    {language.label}
                  </option>
                ))}
              </select>
              <FieldError id="ssml-dictionary-language-error" message={fieldErrors.language} />
            </label>

            <label>
              <span className="field-heading">
                <span className="required">変換元</span>
                <span className="field-example" data-testid="ssml-dictionary-source-example">
                  例: {typeDefinition.sourceExample}
                </span>
              </span>
              <input
                data-testid="ssml-dictionary-source"
                type="text"
                required
                value={form.sourceText}
                maxLength={MAX_SOURCE_LENGTH}
                readOnly={isEditing || saving}
                placeholder={typeDefinition.sourceExample}
                ref={(el) => {
                  fieldRefs.current.source = el;
                }}
                className={fieldErrors.source ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.source ? true : undefined}
                aria-describedby={fieldErrors.source ? 'ssml-dictionary-source-error' : undefined}
                onChange={(e) => updateForm({ sourceText: e.target.value })}
              />
              <FieldError id="ssml-dictionary-source-error" message={fieldErrors.source} />
              <small>
                放送文中でこの文字列に一致した箇所を置き換えます(「/」「&lt;」「&gt;」は使えません)。
                {isEditing
                  ? '編集中は変更できません。変えたい場合は削除して登録し直してください。'
                  : ''}
              </small>
            </label>

            <label>
              <span className="field-heading">
                <span className="required" data-testid="ssml-dictionary-replacement-label">
                  {typeDefinition.replacementLabel}
                </span>
                <span className="field-example" data-testid="ssml-dictionary-replacement-example">
                  例: {typeDefinition.replacementExample}
                </span>
              </span>
              <input
                data-testid="ssml-dictionary-replacement"
                type="text"
                required
                value={form.replacement}
                readOnly={saving}
                ref={(el) => {
                  fieldRefs.current.replacement = el;
                }}
                className={fieldErrors.replacement ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.replacement ? true : undefined}
                aria-describedby={
                  fieldErrors.replacement ? 'ssml-dictionary-replacement-error' : undefined
                }
                maxLength={MAX_REPLACEMENT_LENGTH}
                placeholder={typeDefinition.replacementPlaceholder}
                onChange={(e) => updateForm({ replacement: e.target.value })}
              />
              <FieldError
                id="ssml-dictionary-replacement-error"
                message={fieldErrors.replacement}
              />
              <small data-testid="ssml-dictionary-replacement-hint">
                {typeDefinition.replacementHint}
              </small>
            </label>

            {/* アクセント記号「'」は日本語入力のままでは打ちにくいので、コピーして貼り付けられるようにする */}
            {form.dictionaryType === 'accent' ? (
              <div
                className="ssml-dictionary-accent-copy"
                data-testid="ssml-dictionary-accent-copy"
              >
                <span>アクセント記号(クリックでコピー):</span>
                <CopyableToken token="'" testId="ssml-dictionary-accent-copy-button" />
              </div>
            ) : null}

            {/* アクセント辞典は効き方が語ごとに異なるため、常時注意書きを出す */}
            {form.dictionaryType === 'accent' ? (
              <div
                role="note"
                data-testid="ssml-dictionary-accent-caution"
                style={{
                  background: '#fff8e1',
                  border: '1px solid #f0c36d',
                  borderRadius: 4,
                  padding: '6px 10px',
                  fontSize: 13,
                }}
              >
                {ACCENT_CAUTION_MESSAGE}
              </div>
            ) : null}

            <label>
              <span>補足(任意)</span>
              <textarea
                data-testid="ssml-dictionary-note"
                value={form.note}
                maxLength={MAX_NOTE_LENGTH}
                rows={2}
                readOnly={saving}
                placeholder="出典・確認した実音声の所見など"
                ref={(el) => {
                  fieldRefs.current.note = el;
                }}
                className={fieldErrors.note ? 'is-invalid' : ''}
                aria-invalid={fieldErrors.note ? true : undefined}
                aria-describedby={fieldErrors.note ? 'ssml-dictionary-note-error' : undefined}
                onChange={(e) => updateForm({ note: e.target.value })}
              />
              <FieldError id="ssml-dictionary-note-error" message={fieldErrors.note} />
            </label>

            <label className="sort-filter-radio">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => updateForm({ enabled: e.target.checked })}
                data-testid="ssml-dictionary-enabled"
              />
              有効(オフにすると放送文に適用されません)
            </label>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => void handlePreview()}
                disabled={previewing || form.replacement.trim() === ''}
                data-testid="ssml-dictionary-preview"
              >
                {previewing ? '生成中...' : '▶ 試聴'}
              </button>
              {previewUrl ? (
                <audio
                  controls
                  autoPlay
                  src={previewUrl}
                  data-testid="ssml-dictionary-preview-audio"
                />
              ) : null}
            </div>
            {previewError ? (
              <div
                role="alert"
                className="error-message"
                data-testid="ssml-dictionary-preview-error"
              >
                {previewError}
              </div>
            ) : null}
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            data-testid="ssml-dictionary-cancel-edit"
          >
            キャンセル
          </button>
          <button
            type="submit"
            className="primary"
            disabled={saving}
            data-testid="ssml-dictionary-save"
          >
            {saving ? (
              <>
                <Spinner />
                保存中...
              </>
            ) : isEditing ? (
              '更新'
            ) : (
              '登録'
            )}
          </button>
        </div>
      </form>

      {confirmDialog}

      {/* 新規登録で、同じ言語・同じ変換元が登録済みのときの上書き確認 */}
      <ConfirmDialog
        open={overwriteTarget !== null}
        title="上書きの確認"
        message={
          overwriteTarget
            ? overwriteTarget.dictionary_type === form.dictionaryType
              ? `変換元 "${overwriteTarget.source_text}" は、すでに「${dictionaryTypeLabel(overwriteTarget.dictionary_type)}」として登録されています。\n上書きします。よろしいですか？`
              : `変換元 "${overwriteTarget.source_text}" は、すでに「${dictionaryTypeLabel(overwriteTarget.dictionary_type)}」として登録されています。\n「${dictionaryTypeLabel(form.dictionaryType)}」で上書きします。よろしいですか？\n(同じ言語で同じ変換元は、種別が違っても1件として扱われるため、既存の登録は置き換わります)`
            : ''
        }
        confirmLabel="上書き"
        destructive
        onConfirm={() => {
          setOverwriteTarget(null);
          void executeSave();
        }}
        onCancel={() => setOverwriteTarget(null)}
      />

      {/* アクセント辞典で、置換後にアクセント核「'」が無いまま保存しようとしたときの確認 */}
      <ConfirmDialog
        open={accentMarkConfirmOpen}
        title="アクセント核の確認"
        message={ACCENT_MARK_MISSING_CONFIRM_MESSAGE}
        confirmLabel="保存する"
        onConfirm={handleConfirmSaveWithoutAccentMark}
        onCancel={() => setAccentMarkConfirmOpen(false)}
      />
    </div>
  );
}
